import { ModelType } from "~/lib/constants";

import { SuggestionItem, SuggestionProps } from "./suggestion";

export interface CollectionLinkItem {
  id: string;
  title: string;
  modelType: ModelType;
  reference: string;
  number: string | null;
  avatar: string | null;
}

/**
 * Which category heading a given modelType is filed under. Anything not listed
 * here falls through to "Items", so new modelTypes from the API keep working
 * without a frontend change — they just need an entry here to get their own group.
 */
const CATEGORY_BY_MODEL_TYPE: Record<string, string> = {
  User: "Employees",
  Site: "Sites",
  MeetingEntry: "Meetings",
};

const DEFAULT_CATEGORY = "Items";

/** Display order of the categories in the popup. Unlisted categories sort last, alphabetically. */
const CATEGORY_ORDER = ["Employees", "Items", "Sites", "Meetings"];

export const restructureArrayToSuggestionProps = (
  array: Array<CollectionLinkItem>,
): SuggestionProps[] => {
  const grouped = new Map<string, SuggestionItem[]>();

  array.forEach((element) => {
    const isUser = element.modelType === "User";
    const heading = CATEGORY_BY_MODEL_TYPE[element.modelType] ?? DEFAULT_CATEGORY;

    const suggestionItem: SuggestionItem = {
      label: element.title,
      email: isUser ? element.reference : undefined,
      reference: element.reference,
      image: element.avatar || undefined,
      id: element.id,
      modelType: element.modelType as SuggestionItem["modelType"],
      type: isUser ? "user" : "item",
    };

    const bucket = grouped.get(heading);
    if (bucket) {
      bucket.push(suggestionItem);
    } else {
      grouped.set(heading, [suggestionItem]);
    }
  });

  return [...grouped.entries()]
    .filter(([, children]) => children.length > 0)
    .sort(([a], [b]) => {
      const rankA = CATEGORY_ORDER.indexOf(a);
      const rankB = CATEGORY_ORDER.indexOf(b);
      if (rankA !== -1 && rankB !== -1) return rankA - rankB;
      if (rankA !== -1) return -1;
      if (rankB !== -1) return 1;
      return a.localeCompare(b);
    })
    .map(([heading, children]) => ({
      type: heading === "Employees" ? ("user" as const) : ("item" as const),
      heading,
      children,
    }));
};
